import {
  AmountElement,
  CardElement,
  type CardElementError,
  type CardElementEvent,
  type CardElementRef,
  Elements,
  OfferFieldsElement,
  type OnBinLookupResult,
  type OnSuccessPayload,
  PayerElement,
} from '@flywire/internal-react-elements';
import { Button } from 'components/ui/Button/Button';
import { LoadingDots } from 'components/ui/LoadingDots/LoadingDots';
import { SENTRY_IGNORED_CARD_FORM_INTERNAL_ERRORS } from 'constants/index';
import { useEnvironment } from 'hooks/useEnvironment/useEnvironment';
import type { PropsWithChildren, RefObject } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentLocale } from 'selectors/ui/ui';
import { logger } from 'services/logger/logger';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

import '@flywire/internal-react-elements/styles.css';
import './CardForm.scss';

type CardFormProps = {
  cardFormRef: RefObject<CardElementRef>;
  currency?: string;
  sessionId: string;
  onBinLookup?: (result: OnBinLookupResult) => void;
  onError?: (error: CardElementError) => void;
  onSuccess: (data: OnSuccessPayload) => void;
  onStartLoading: () => void;
  onEndLoading: () => void;
  onEvent?: (event: CardElementEvent) => void;
};

const CardForm = ({
  cardFormRef,
  currency,
  children,
  sessionId,
  onBinLookup,
  onError,
  onSuccess,
  onEvent,
  onStartLoading,
  onEndLoading,
}: PropsWithChildren<CardFormProps>) => {
  const { environment } = useEnvironment();
  const locale = useSelector(getCurrentLocale);

  const _onError = async (error: CardElementError) => {
    logger.log({
      description: 'card_form_error',
      parameters: {
        type: error.type,
        sessionId: sessionId,
        extra: 'extra' in error ? error.extra : undefined,
        errors: 'errors' in error ? error.errors : undefined,
      },
    });

    switch (error.type) {
      case 'challenge_error':
      case 'invalid_card_info':
      case 'invalid_payer_info':
      case 'not_found':
        break;
      case 'internal_error':
        if (
          !SENTRY_IGNORED_CARD_FORM_INTERNAL_ERRORS.includes(
            error.extra.message,
          )
        ) {
          errorNotifier.notify(
            new Error(`CardForm error: ${JSON.stringify(error)}`),
          );
        }
    }

    onEndLoading();
    onError?.(error);
  };

  const _onEvent = (event: CardElementEvent) => {
    logger.log({
      description: 'card_form_event',
      parameters: {
        type: event.type,
        checkoutSessionId: sessionId,
        extra: 'extra' in event ? event.extra : undefined,
        errors: 'errors' in event ? event.errors : undefined,
      },
    });

    onEvent?.(event);
  };

  return (
    <Elements environment={environment} locale={locale}>
      <CardElement
        currency={currency}
        onBinLookup={onBinLookup}
        onEndLoading={onEndLoading}
        onError={_onError}
        onStartLoading={onStartLoading}
        onSuccess={onSuccess}
        sessionId={sessionId}
        onEvent={_onEvent}
        ref={cardFormRef}
      >
        {children}
      </CardElement>
    </Elements>
  );
};

const Form = ({
  buttonDisabled = false,
  buttonLabel,
  isLoading = false,
  onSubmit,
  withAmount = false,
}: {
  buttonDisabled?: boolean;
  buttonLabel: string;
  isLoading?: boolean;
  onSubmit?: () => void;
  withAmount?: boolean;
}) => {
  return (
    <div className="CheckoutCardForm-fields flex flex-col h-full">
      <div className="flex-1">
        <PayerElement className="CheckoutCardForm-payer">
          <PayerElement.FirstName className="CheckoutCardForm-firstName" />
          <PayerElement.LastName />
        </PayerElement>
        <CardElement.CardNumber className="CheckoutCardForm-cardNumber" />
        <div className="CheckoutCardForm-cardDetails">
          <CardElement.ExpiryDate />
          <CardElement.CVV />
        </div>
        <div className="mb-8">
          <OfferFieldsElement />
        </div>
        {withAmount && <AmountElement className="mb-9" />}
      </div>
      <div className="pt-6 pb-4">
        <Button
          tone="primary"
          size="full"
          data-testid="cardFormSubmitBtn"
          onClick={onSubmit}
          disabled={buttonDisabled}
        >
          {isLoading ? (
            <LoadingDots className="h-6 items-center [&>span]:bg-white" />
          ) : (
            buttonLabel
          )}
        </Button>
      </div>
    </div>
  );
};

const Fields = ({ withAmount = false }: { withAmount?: boolean }) => {
  return (
    <div className="CheckoutCardForm-fields">
      <PayerElement className="CheckoutCardForm-payer">
        <PayerElement.FirstName className="CheckoutCardForm-firstName" />
        <PayerElement.LastName />
      </PayerElement>
      <CardElement.CardNumber className="CheckoutCardForm-cardNumber" />
      <div className="CheckoutCardForm-cardDetails">
        <CardElement.ExpiryDate />
        <CardElement.CVV />
      </div>
      {withAmount && <AmountElement className="mb-9" />}
    </div>
  );
};

const ButtonComponent = ({
  isDisabled = false,
  label,
  isLoading = false,
  onSubmit,
}: {
  isDisabled?: boolean;
  label: string;
  isLoading?: boolean;
  onSubmit?: () => void;
}) => {
  return (
    <Button
      tone="primary"
      size="full"
      data-testid="cardFormSubmitBtn"
      onClick={onSubmit}
      disabled={isDisabled}
    >
      {isLoading ? (
        <LoadingDots className="h-6 items-center [&>span]:bg-white" />
      ) : (
        label
      )}
    </Button>
  );
};

CardForm.Form = Form;
CardForm.Fields = Fields;
CardForm.Button = ButtonComponent;

export { CardForm };
